import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

interface ReviseNoteModalProps {
  onSubmit: (instruction: string) => void;
  onClose: () => void;
}

export function ReviseNoteModal({ onSubmit, onClose }: ReviseNoteModalProps) {
  const [instruction, setInstruction] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const trimmed = instruction.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  // Mounted only while open (no `open` prop); Radix still owns Escape, the
  // focus trap, and restoring focus to the opener on unmount.
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/10"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
        className="top-auto bottom-20 block w-full max-w-lg translate-y-0 gap-0 rounded-xl border-border bg-surface-raised p-3 shadow-2xl sm:bottom-32 sm:max-w-lg sm:rounded-2xl sm:p-6"
      >
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div>
            <DialogTitle className="text-[11px] sm:text-base font-medium text-foreground">Revise note</DialogTitle>
            <DialogDescription className="text-[9px] sm:text-sm text-text-tertiary mt-0.5">Describe the revision (quotes added automatically)</DialogDescription>
          </div>
          <button onClick={onClose} aria-label="Close revise note" className="p-1 sm:p-2 hover:bg-surface-hover rounded-md sm:rounded-lg transition-colors">
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-text-tertiary" />
          </button>
        </div>

        <textarea
          ref={textareaRef}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="e.g. add medication section, include last lab values"
          className="w-full px-2 sm:px-3 py-2 sm:py-3 text-[11px] sm:text-sm bg-surface-raised text-foreground border border-border rounded-md sm:rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent resize-none placeholder:text-text-tertiary"
          rows={3}
        />

        <div className="flex items-center gap-2 mt-3 sm:mt-4">
          <button
            onClick={handleSubmit}
            disabled={!instruction.trim()}
            className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-md sm:rounded-lg font-medium text-[10px] sm:text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:bg-surface-sunken disabled:text-text-tertiary disabled:cursor-not-allowed transition-colors"
          >
            Send revision
          </button>
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-md sm:rounded-lg font-medium text-[10px] sm:text-sm text-foreground hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
